import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { Except, Writable } from "type-fest";
import { z } from "zod";
import type { Resource} from "./resource";
import { resourceButNotResourceGroupZodSchema } from "./resource";
import type {
    ResourceGroup} from "./resource-group";
import {
    isNotResourceGroup,
    isResourceGroup,
    resourceGroupZodObject,
} from "./resource-group";
import type {
    ResourceId} from "./resource-id";
import {
    resourceIdFactory,
    resourceIdZodSchema,
} from "./resource-id";

/** Node in graph one for each ResourceGroup */
type ResourceGraph = {
    readonly _id: ResourceId;
    readonly _type: "RESOURCE_GRAPH";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly children: (Exclude<Resource, ResourceGroup> | ResourceGraph)[];
    readonly parentId?: ResourceId;
    readonly resourceGroup: ResourceGroup;
};

type FactoryParams = Except<ResourceGraph, "_type" | "_id" | "_createdAt">;

const resourceGraphFactory = (params: FactoryParams): ResourceGraph => {
    const input = params;
    const graph: ResourceGraph = {
        ...input,
        _id: input.resourceGroup?._id ?? resourceIdFactory(),
        _type: "RESOURCE_GRAPH" as const,
        _createdAt: nowIsoAndUnix(),
    };
    return graph;
};

const resourceGraphZodSchema: z.ZodSchema<ResourceGraph> = z.object({
    _id: resourceIdZodSchema,
    _type: z.literal("RESOURCE_GRAPH"),
    _createdAt: isoAndUnixZodSchema,
    parentId: resourceIdZodSchema.optional(),
    children: z.lazy(() =>
        z.array(
            z.union([
                resourceButNotResourceGroupZodSchema,
                resourceGraphZodSchema,
            ]),
        ),
    ),
    resourceGroup: resourceGroupZodObject,
});

const isResourceGraph = (
    item: Resource | ResourceGraph,
): item is ResourceGraph => item._type === "RESOURCE_GRAPH";
const isNotResourceGraph = (
    item: Resource | ResourceGraph,
): item is Exclude<Resource, ResourceGroup> => item._type !== "RESOURCE_GRAPH";

const makeResourceGraphMaxDepth = 1000;
const makeResourceGraph = (
    resourceGroup: ResourceGroup,
    allResources: Resource[],
    parentId?: ResourceId,
    depth = 0,
): ResourceGraph => {
    if (depth > makeResourceGraphMaxDepth)
        throw new Error("Make graph max depth exceeded");
    if (!isResourceGroup(resourceGroup))
        throw new Error("Root resource group required");

    // const resources = allResources.filter(resource =>
    //     resourceGroup.resources
    //         .map(resource => resource._id)
    //         .includes(resource._id)
    // );
    const resources = resourceGroup.resources.flatMap(resourceLink => {
        const resource = allResources.find(
            resource => resource._id === resourceLink._id,
        );
        return resource ? [resource] : [];
    });

    const newGraph: Writable<ResourceGraph> = resourceGraphFactory({
        parentId,
        resourceGroup: resourceGroup,
        children: [],
    });

    const resourceGroups = resources.filter(isResourceGroup);
    const otherResources = resources.filter(isNotResourceGroup);
    const graphs = resourceGroups.map(group =>
        makeResourceGraph(group, allResources, newGraph._id, depth + 1),
    );

    newGraph.children = [...otherResources, ...graphs];

    return newGraph as ResourceGraph;
};

export {
    isNotResourceGraph,
    isResourceGraph,
    makeResourceGraph,
    resourceGraphFactory,
    resourceGraphZodSchema,
};
export type { ResourceGraph };
